import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '../lib/supabase'

const fmt  = (n, d=2) => n != null && n !== '' ? Number(n).toLocaleString('en-US',{minimumFractionDigits:d,maximumFractionDigits:d}) : '—'
const fmtP = (n) => n != null ? (n>=0?'+':'')+Number(n).toFixed(1)+'%' : '—'
const monthLabel = (m) => { if(!m) return '—'; const [y,mo]=m.split('-'); const names=['January','February','March','April','May','June','July','August','September','October','November','December']; return `${names[parseInt(mo,10)-1]||mo} ${y}` }
const thisMonth = () => new Date().toISOString().slice(0,7)+'-01'

const XIcon      = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
const PlusIcon   = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
const TrashIcon  = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/></svg>

function Toast({ message, type, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 3000); return () => clearTimeout(t) }, [onClose])
  return (
    <div className={`fixed bottom-6 right-6 z-50 px-5 py-3 rounded-xl shadow-lg text-sm font-medium text-white ${type==='error'?'bg-red-600':'bg-[#1A7A4A]'}`}>
      {message}
    </div>
  )
}

const ConfirmOverlay = ({ message, onConfirm, onCancel }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center">
    <div className="absolute inset-0 bg-black/40" onClick={onCancel}/>
    <div className="relative bg-white rounded-xl shadow-2xl p-6 w-80">
      <p className="text-sm text-[#1C1A18] mb-5">{message}</p>
      <div className="flex gap-2 justify-end">
        <button onClick={onCancel} className="px-4 py-2 text-sm border border-[#E5E7EB] rounded-lg">Cancel</button>
        <button onClick={onConfirm} className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg">Delete</button>
      </div>
    </div>
  </div>
)

const EMPTY_ROW = {
  boq_item_id: '', is_new_item: false, description: '', unit: '',
  month: thisMonth(), fqac: '', fqac_change_reason: '', monthly_progress: '',
  cost_code_id: '', sub_code: '', notes: '',
}

export default function QSQuantityTracking({ initProject }) {
  const [projects, setProjects]             = useState([])
  const [selectedProject, setSelectedProject] = useState(null)
  const [activeTab, setActiveTab]           = useState('entries') // 'entries' | 'comparison'

  const [entries, setEntries]               = useState([])
  const [boqItems, setBoqItems]             = useState([])
  const [costCodes, setCostCodes]           = useState([])
  const [subCodes, setSubCodes]             = useState([]) // {sub_code, description}
  const [budgetBySubCode, setBudgetBySubCode] = useState({}) // sub_code -> {qty, description}

  const [loading, setLoading]               = useState(false)
  const [toast, setToast]                   = useState(null)
  const [confirmDelete, setConfirmDelete]   = useState(null)

  // Filters
  const [monthFilter, setMonthFilter]       = useState('all')
  const [statusFilter, setStatusFilter]     = useState('all')

  // Entry modal
  const [showModal, setShowModal]           = useState(false)
  const [editEntry, setEditEntry]           = useState(null)
  const [form, setForm]                     = useState({ ...EMPTY_ROW })

  const showToast = (msg, type='success') => setToast({ message: msg, type })
  const h = (k, v) => setForm(f => ({ ...f, [k]: v }))

  useEffect(() => {
    supabase.from('projects').select('id,name').order('name').then(({ data }) => setProjects(data||[]))
  }, [])

  useEffect(() => { if (initProject) setSelectedProject(initProject) }, [initProject])

  useEffect(() => {
    if (selectedProject?.id) {
      loadEntries(selectedProject.id)
      loadBoqItems(selectedProject.id)
      loadCostCodes(selectedProject.id)
      loadSubCodesAndBudget(selectedProject.id)
    }
  }, [selectedProject?.id]) // eslint-disable-line

  const loadEntries = useCallback(async (pid) => {
    setLoading(true)
    const { data } = await supabase.from('qs_quantity_tracking').select('*').eq('project_id', pid).order('month', { ascending: false })
    setEntries(data || [])
    setLoading(false)
  }, [])

  const loadBoqItems = async (pid) => {
    const { data } = await supabase.from('boq_items')
      .select('id, item_no, description, unit, qty_tendered, cost_code')
      .eq('project_id', pid).eq('is_section_header', false).order('sort_order')
    setBoqItems(data || [])
  }

  const loadCostCodes = async (pid) => {
    const { data } = await supabase.from('cost_codes').select('id,code,description').eq('project_id', pid).eq('level', 1).eq('is_active', true).order('code')
    setCostCodes(data || [])
  }

  // Same source pattern as Rate Analysis / Labour Productivity — budget qty per sub_code
  const loadSubCodesAndBudget = async (pid) => {
    const { data } = await supabase.from('tender_direct_items')
      .select('budget_material_code, labour_code, budget_sc_code, budget_equipment_code, row_type, description, resource_description, qty')
      .eq('project_id', pid).in('row_type', ['material', 'labour', 'subcontract', 'equipment'])
    const map = {}
    ;(data||[]).forEach(r => {
      let sc = null
      if (r.row_type === 'labour')           sc = r.labour_code
      else if (r.row_type === 'material')    sc = r.budget_material_code
      else if (r.row_type === 'subcontract') sc = r.budget_sc_code
      else if (r.row_type === 'equipment')   sc = r.budget_equipment_code
      if (!sc) return
      if (!map[sc]) map[sc] = { sub_code: sc, description: r.resource_description || r.description || '', budget_qty: 0 }
      map[sc].budget_qty += parseFloat(r.qty) || 0
    })
    setSubCodes(Object.values(map).sort((a,b)=>a.sub_code.localeCompare(b.sub_code)))
    setBudgetBySubCode(map)
  }

  // ── Auto-detect FQAC change vs last month for the same BOQ item / description ──
  const getPreviousFqac = (boqItemId, description, currentId) => {
    const rowsForItem = entries.filter(e =>
      e.id !== currentId &&
      (boqItemId ? e.boq_item_id === boqItemId : e.description === description)
    ).sort((a,b) => b.month.localeCompare(a.month))
    return rowsForItem[0]?.fqac ?? null
  }

  // ── Save entry ────────────────────────────────────────────
  const handleSave = async () => {
    if (!form.month) return showToast('الشهر مطلوب', 'error')
    if (!form.is_new_item && !form.boq_item_id) return showToast('اختر بند BOQ أو حدده كبند جديد', 'error')
    if (form.is_new_item && !form.description.trim()) return showToast('وصف البند الجديد مطلوب', 'error')
    if (!form.fqac) return showToast('FQAC مطلوب', 'error')

    const prevFqac = getPreviousFqac(form.boq_item_id || null, form.description, editEntry?.id)
    const fqacChanged = prevFqac != null && parseFloat(prevFqac) !== parseFloat(form.fqac)
    if (fqacChanged && !form.fqac_change_reason.trim()) {
      return showToast(`FQAC اتغير من ${prevFqac} لـ ${form.fqac} — لازم تكتب السبب`, 'error')
    }

    const payload = {
      project_id:          selectedProject.id,
      month:               form.month,
      boq_item_id:         form.is_new_item ? null : (form.boq_item_id || null),
      is_new_item:         form.is_new_item,
      description:         form.description.trim() || null,
      unit:                form.unit.trim() || null,
      fqac:                parseFloat(form.fqac) || 0,
      fqac_change_reason:  fqacChanged ? form.fqac_change_reason.trim() : null,
      monthly_progress:    parseFloat(form.monthly_progress) || 0,
      cost_code_id:        form.cost_code_id || null,
      sub_code:            form.sub_code.trim() || null,
      status:              (form.cost_code_id && form.sub_code.trim()) ? 'coded' : 'draft',
      notes:               form.notes.trim() || null,
    }

    const { error } = editEntry
      ? await supabase.from('qs_quantity_tracking').update(payload).eq('id', editEntry.id)
      : await supabase.from('qs_quantity_tracking').insert(payload)

    if (error) return showToast('فشل: ' + error.message, 'error')
    showToast(editEntry ? 'تم التحديث' : 'تم الحفظ')
    setShowModal(false)
    loadEntries(selectedProject.id)
  }

  // ── Inline coding from the table ───────────────────────────
  const handleCodeInline = async (row, field, value) => {
    const patch = { [field]: value || null }
    if (field === 'cost_code_id' || field === 'sub_code') {
      const cc = field === 'cost_code_id' ? value : row.cost_code_id
      const sc = field === 'sub_code' ? value : row.sub_code
      patch.status = (cc && sc) ? 'coded' : 'draft'
    }
    const { error } = await supabase.from('qs_quantity_tracking').update(patch).eq('id', row.id)
    if (error) return showToast('فشل: ' + error.message, 'error')
    setEntries(prev => prev.map(e => e.id === row.id ? { ...e, ...patch } : e))
  }

  const handleDelete = async (id) => {
    await supabase.from('qs_quantity_tracking').delete().eq('id', id)
    showToast('تم الحذف')
    setConfirmDelete(null)
    loadEntries(selectedProject.id)
  }

  const openEdit = (e) => {
    setEditEntry(e)
    setForm({
      boq_item_id: e.boq_item_id || '', is_new_item: e.is_new_item, description: e.description || '',
      unit: e.unit || '', month: e.month, fqac: e.fqac, fqac_change_reason: e.fqac_change_reason || '',
      monthly_progress: e.monthly_progress, cost_code_id: e.cost_code_id || '', sub_code: e.sub_code || '',
      notes: e.notes || '',
    })
    setShowModal(true)
  }

  // ── Filters ───────────────────────────────────────────────
  const uniqueMonths = [...new Set(entries.map(e => e.month))].sort().reverse()
  const filtered = entries.filter(e => {
    if (monthFilter !== 'all' && e.month !== monthFilter) return false
    if (statusFilter !== 'all' && e.status !== statusFilter) return false
    return true
  })

  const boqMap = Object.fromEntries(boqItems.map(b => [b.id, b]))
  const ccMap  = Object.fromEntries(costCodes.map(c => [c.id, c]))

  // ── Comparison data: latest FQAC + cumulative progress per tracked item ────
  const groupKey = (e) => e.boq_item_id || `new:${e.description}`
  const comparisonRows = (() => {
    const groups = {}
    entries.forEach(e => {
      const key = groupKey(e)
      if (!groups[key]) groups[key] = []
      groups[key].push(e)
    })
    return Object.entries(groups).map(([key, rows]) => {
      const sorted = [...rows].sort((a,b) => a.month.localeCompare(b.month))
      const latest = sorted[sorted.length - 1]
      const cumulativeProgress = sorted.reduce((s,r) => s + (parseFloat(r.monthly_progress)||0), 0)
      const boq = latest.boq_item_id ? boqMap[latest.boq_item_id] : null
      const budgetQty = latest.sub_code ? (budgetBySubCode[latest.sub_code]?.budget_qty || 0) : 0
      const fqac = parseFloat(latest.fqac) || 0
      const pctComplete = fqac > 0 ? (cumulativeProgress / fqac) * 100 : 0
      return {
        key,
        isNew: latest.is_new_item,
        description: boq?.description || latest.description || '—',
        unit: boq?.unit || latest.unit || '—',
        boqQty: boq ? parseFloat(boq.qty_tendered)||0 : null,
        budgetQty,
        fqac,
        cumulativeProgress,
        pctComplete,
        costCode: latest.cost_code_id ? ccMap[latest.cost_code_id]?.code : null,
        subCode: latest.sub_code,
        lastUpdated: latest.month,
        changeReasons: sorted.filter(r => r.fqac_change_reason).map(r => ({ month: r.month, reason: r.fqac_change_reason, fqac: r.fqac })),
      }
    }).sort((a,b) => (a.description||'').localeCompare(b.description||''))
  })()

  const statusBadge = (status) => status === 'coded'
    ? <span style={{background:'#FEF3C7',color:'#B45309',padding:'2px 9px',borderRadius:10,fontSize:10.5,fontWeight:700}}>Coded</span>
    : <span style={{background:'#F3F4F6',color:'#6B7280',padding:'2px 9px',borderRadius:10,fontSize:10.5,fontWeight:700}}>Draft</span>

  return (
    <div className="min-h-screen bg-[#F9FAFB] p-6">
      {toast && <Toast {...toast} onClose={() => setToast(null)}/>}
      {confirmDelete && (
        <ConfirmOverlay message="متأكد من حذف السجل ده؟" onConfirm={() => handleDelete(confirmDelete)} onCancel={() => setConfirmDelete(null)}/>
      )}

      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="text-xl font-bold text-[#1C1A18]">QS Quantity Tracking</h1>
            <p className="text-xs text-[#6B7280] mt-0.5">Monthly Forecast Quantity at Completion (FQAC) and Progress per BOQ item, tracked over time.</p>
          </div>
          <select value={selectedProject?.id || ''} onChange={e => setSelectedProject(projects.find(p => p.id === e.target.value) || null)}
            className="border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm bg-white">
            <option value="">— Select Project —</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>

        {selectedProject && (
          <>
            {/* Tabs */}
            <div style={{display:'flex',gap:8,borderBottom:'1px solid #E5E7EB',marginBottom:16}}>
              <button onClick={()=>setActiveTab('entries')}
                style={{padding:'10px 4px',marginBottom:-1,fontSize:13,fontWeight:600,background:'none',border:'none',borderBottom:activeTab==='entries'?'2px solid #1B3A6B':'2px solid transparent',color:activeTab==='entries'?'#1B3A6B':'#6B7280',cursor:'pointer'}}>
                📝 Entries
              </button>
              <button onClick={()=>setActiveTab('comparison')}
                style={{padding:'10px 4px',marginBottom:-1,fontSize:13,fontWeight:600,background:'none',border:'none',borderBottom:activeTab==='comparison'?'2px solid #1B3A6B':'2px solid transparent',color:activeTab==='comparison'?'#1B3A6B':'#6B7280',cursor:'pointer'}}>
                📊 Comparison
              </button>
            </div>

            {activeTab === 'entries' ? (
              <>
                {/* Toolbar */}
                <div className="bg-white rounded-xl border border-[#E5E7EB] p-4 mb-4 flex flex-wrap items-center gap-3">
                  <select value={monthFilter} onChange={e=>setMonthFilter(e.target.value)} className="border border-[#E5E7EB] rounded-lg px-3 py-1.5 text-xs">
                    <option value="all">All Months</option>
                    {uniqueMonths.map(m => <option key={m} value={m}>{monthLabel(m)}</option>)}
                  </select>
                  <select value={statusFilter} onChange={e=>setStatusFilter(e.target.value)} className="border border-[#E5E7EB] rounded-lg px-3 py-1.5 text-xs">
                    <option value="all">All Status</option>
                    <option value="draft">Draft</option>
                    <option value="coded">Coded</option>
                  </select>
                  <div className="flex-1"/>
                  <button onClick={() => { setForm({...EMPTY_ROW}); setEditEntry(null); setShowModal(true) }}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-[#1B3A6B] text-white rounded-lg text-xs font-medium">
                    <PlusIcon/> Add Entry
                  </button>
                </div>

                {/* Table */}
                <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                        <tr>
                          <th className="px-3 py-3 text-left text-[#6B7280] uppercase font-semibold">Month</th>
                          <th className="px-3 py-3 text-left text-[#6B7280] uppercase font-semibold">Item</th>
                          <th className="px-3 py-3 text-left text-[#6B7280] uppercase font-semibold">Unit</th>
                          <th className="px-3 py-3 text-right text-[#6B7280] uppercase font-semibold">FQAC</th>
                          <th className="px-3 py-3 text-left text-[#6B7280] uppercase font-semibold">Change Reason</th>
                          <th className="px-3 py-3 text-right text-[#6B7280] uppercase font-semibold">Progress (Month)</th>
                          <th className="px-3 py-3 text-left text-[#6B7280] uppercase font-semibold">Cost Code</th>
                          <th className="px-3 py-3 text-left text-[#6B7280] uppercase font-semibold">Sub Code</th>
                          <th className="px-3 py-3 text-left text-[#6B7280] uppercase font-semibold">Status</th>
                          <th className="px-3 py-3"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#F3F4F6]">
                        {loading ? (
                          <tr><td colSpan={10} className="text-center py-8 text-[#9CA3AF]">Loading...</td></tr>
                        ) : filtered.length === 0 ? (
                          <tr><td colSpan={10} className="text-center py-8 text-[#9CA3AF]">لا يوجد بيانات</td></tr>
                        ) : filtered.map(e => {
                          const boq = e.boq_item_id ? boqMap[e.boq_item_id] : null
                          return (
                            <tr key={e.id} className="hover:bg-[#FAFAFA]">
                              <td className="px-3 py-2 whitespace-nowrap">{monthLabel(e.month)}</td>
                              <td className="px-3 py-2 max-w-[200px] truncate" title={boq?.description || e.description}>
                                {e.is_new_item && <span className="text-amber-600 font-bold mr-1">[NEW]</span>}
                                {boq?.description || e.description || '—'}
                              </td>
                              <td className="px-3 py-2 text-[#6B7280]">{boq?.unit || e.unit || '—'}</td>
                              <td className="px-3 py-2 text-right font-semibold">{fmt(e.fqac)}</td>
                              <td className="px-3 py-2 text-amber-700 max-w-[160px] truncate" title={e.fqac_change_reason}>{e.fqac_change_reason || '—'}</td>
                              <td className="px-3 py-2 text-right">{fmt(e.monthly_progress)}</td>
                              <td className="px-3 py-2">
                                <select value={e.cost_code_id || ''} onChange={ev => handleCodeInline(e, 'cost_code_id', ev.target.value)}
                                  className="border border-[#E5E7EB] rounded px-2 py-1 text-xs max-w-[110px]">
                                  <option value="">— —</option>
                                  {costCodes.map(c => <option key={c.id} value={c.id}>{c.code}</option>)}
                                </select>
                              </td>
                              <td className="px-3 py-2">
                                <select value={e.sub_code || ''} onChange={ev => handleCodeInline(e, 'sub_code', ev.target.value)}
                                  className="border border-[#E5E7EB] rounded px-2 py-1 text-xs font-mono max-w-[130px]">
                                  <option value="">— —</option>
                                  {subCodes.map(s => <option key={s.sub_code} value={s.sub_code}>{s.sub_code}</option>)}
                                </select>
                              </td>
                              <td className="px-3 py-2">{statusBadge(e.status)}</td>
                              <td className="px-3 py-2">
                                <div className="flex items-center gap-2">
                                  <button onClick={() => openEdit(e)} className="text-[#6B7280] hover:text-[#1B3A6B]">✏</button>
                                  <button onClick={() => setConfirmDelete(e.id)} className="text-red-400 hover:text-red-600"><TrashIcon/></button>
                                </div>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            ) : (
              <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
                <div style={{padding:'14px 16px', borderBottom:'1px solid #F3F4F6', background:'#F9FAFB'}}>
                  <p style={{fontSize:13, fontWeight:700, color:'#0F1017'}}>Quantity Comparison</p>
                  <p style={{fontSize:11, color:'#6B7280', marginTop:2}}>BOQ Qty vs Budget Qty vs latest Forecast at Completion (FQAC) vs cumulative Progress, per tracked item.</p>
                </div>
                <div style={{overflowX:'auto'}}>
                  <table style={{width:'100%', borderCollapse:'collapse', fontSize:12.5}}>
                    <thead style={{background:'#F9FAFB', borderBottom:'2px solid #E5E7EB'}}>
                      <tr>
                        <th style={{textAlign:'left',padding:'10px 12px',fontSize:11,fontWeight:700,color:'#374151',textTransform:'uppercase'}}>Item</th>
                        <th style={{textAlign:'left',padding:'10px 12px',fontSize:11,fontWeight:700,color:'#374151',textTransform:'uppercase'}}>Cost/Sub Code</th>
                        <th style={{textAlign:'right',padding:'10px 12px',fontSize:11,fontWeight:700,color:'#374151',textTransform:'uppercase'}}>BOQ Qty</th>
                        <th style={{textAlign:'right',padding:'10px 12px',fontSize:11,fontWeight:700,color:'#374151',textTransform:'uppercase'}}>Budget Qty</th>
                        <th style={{textAlign:'right',padding:'10px 12px',fontSize:11,fontWeight:700,color:'#374151',textTransform:'uppercase'}}>FQAC</th>
                        <th style={{textAlign:'right',padding:'10px 12px',fontSize:11,fontWeight:700,color:'#374151',textTransform:'uppercase'}}>Cumulative Progress</th>
                        <th style={{textAlign:'right',padding:'10px 12px',fontSize:11,fontWeight:700,color:'#374151',textTransform:'uppercase'}}>% Complete</th>
                        <th style={{textAlign:'left',padding:'10px 12px',fontSize:11,fontWeight:700,color:'#374151',textTransform:'uppercase'}}>Last Updated</th>
                      </tr>
                    </thead>
                    <tbody>
                      {comparisonRows.length === 0 ? (
                        <tr><td colSpan={8} style={{padding:32,textAlign:'center',color:'#9CA3AF'}}>لا يوجد بيانات</td></tr>
                      ) : comparisonRows.map(r => (
                        <tr key={r.key} style={{borderBottom:'1px solid #F3F4F6'}}>
                          <td style={{padding:'9px 12px'}}>
                            {r.isNew && <span style={{color:'#D97706',fontWeight:700,marginRight:4}}>[NEW]</span>}
                            {r.description}
                            <div style={{fontSize:10,color:'#9CA3AF'}}>{r.unit}</div>
                          </td>
                          <td style={{padding:'9px 12px',fontFamily:'monospace',fontSize:11}}>
                            {r.costCode || '—'} / {r.subCode || '—'}
                          </td>
                          <td style={{padding:'9px 12px',textAlign:'right'}}>{r.boqQty != null ? fmt(r.boqQty) : '—'}</td>
                          <td style={{padding:'9px 12px',textAlign:'right'}}>{r.budgetQty > 0 ? fmt(r.budgetQty) : '—'}</td>
                          <td style={{padding:'9px 12px',textAlign:'right',fontWeight:700}}>{fmt(r.fqac)}</td>
                          <td style={{padding:'9px 12px',textAlign:'right'}}>{fmt(r.cumulativeProgress)}</td>
                          <td style={{padding:'9px 12px',textAlign:'right',fontWeight:700,color:r.pctComplete>100?'#DC2626':'#1A7A4A'}}>{r.fqac>0?r.pctComplete.toFixed(1)+'%':'—'}</td>
                          <td style={{padding:'9px 12px',color:'#6B7280'}}>{monthLabel(r.lastUpdated)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Change log */}
                {comparisonRows.some(r => r.changeReasons.length > 0) && (
                  <div style={{padding:16, borderTop:'1px solid #F3F4F6'}}>
                    <p style={{fontSize:12,fontWeight:700,color:'#374151',marginBottom:8}}>FQAC Change Log</p>
                    <div style={{display:'flex',flexDirection:'column',gap:6}}>
                      {comparisonRows.filter(r=>r.changeReasons.length>0).map(r => (
                        <div key={r.key} style={{fontSize:11.5,background:'#FFFBEB',border:'1px solid #FDE68A',borderRadius:8,padding:'8px 12px'}}>
                          <span style={{fontWeight:700,color:'#374151'}}>{r.description}</span>
                          {r.changeReasons.map((c,i) => (
                            <div key={i} style={{color:'#92400E',marginTop:2}}>
                              {monthLabel(c.month)} → FQAC: {fmt(c.fqac)} — {c.reason}
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Add / Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-[#E5E7EB]">
              <h2 className="text-base font-bold text-[#1C1A18]">{editEntry ? 'Edit Entry' : 'Add Entry'}</h2>
              <button onClick={() => setShowModal(false)}><XIcon/></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="flex items-center gap-2 text-xs font-medium text-[#6B7280] mb-2">
                  <input type="checkbox" checked={form.is_new_item} onChange={e=>h('is_new_item', e.target.checked)}/>
                  This is a new tracking item (not in BOQ)
                </label>
              </div>

              {form.is_new_item ? (
                <div>
                  <label className="block text-xs font-medium text-[#6B7280] mb-1">Description *</label>
                  <input value={form.description} onChange={e=>h('description', e.target.value)}
                    placeholder="e.g. Additional retaining wall — Zone C"
                    className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm"/>
                  <input value={form.unit} onChange={e=>h('unit', e.target.value)} placeholder="Unit (e.g. M3)"
                    className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm mt-2"/>
                </div>
              ) : (
                <div>
                  <label className="block text-xs font-medium text-[#6B7280] mb-1">BOQ Item *</label>
                  <select value={form.boq_item_id} onChange={e=>h('boq_item_id', e.target.value)}
                    className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm">
                    <option value="">— Select BOQ Item —</option>
                    {boqItems.map(b => <option key={b.id} value={b.id}>{b.item_no ? b.item_no+' — ' : ''}{b.description}</option>)}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-[#6B7280] mb-1">Month *</label>
                <input type="month" value={form.month?.slice(0,7)} onChange={e=>h('month', e.target.value+'-01')}
                  className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm"/>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-[#6B7280] mb-1">FQAC (Forecast at Completion) *</label>
                  <input type="number" value={form.fqac} onChange={e=>h('fqac', e.target.value)} placeholder="e.g. 5500"
                    className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm"/>
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#6B7280] mb-1">Progress This Month *</label>
                  <input type="number" value={form.monthly_progress} onChange={e=>h('monthly_progress', e.target.value)} placeholder="e.g. 320"
                    className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm"/>
                </div>
              </div>

              {(() => {
                const prevFqac = getPreviousFqac(form.boq_item_id || null, form.description, editEntry?.id)
                const changed = prevFqac != null && form.fqac && parseFloat(prevFqac) !== parseFloat(form.fqac)
                return changed ? (
                  <div>
                    <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-700 mb-2">
                      ⚠ FQAC اتغير من <b>{fmt(prevFqac)}</b> لـ <b>{fmt(form.fqac)}</b> — لازم توضح السبب
                    </div>
                    <label className="block text-xs font-medium text-[#6B7280] mb-1">Reason for Change *</label>
                    <input value={form.fqac_change_reason} onChange={e=>h('fqac_change_reason', e.target.value)}
                      placeholder="e.g. Design revision approved by client"
                      className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm"/>
                  </div>
                ) : null
              })()}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-[#6B7280] mb-1">Cost Code (Cost Control coding)</label>
                  <select value={form.cost_code_id} onChange={e=>h('cost_code_id', e.target.value)}
                    className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm">
                    <option value="">— None —</option>
                    {costCodes.map(c => <option key={c.id} value={c.id}>{c.code} — {c.description}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#6B7280] mb-1">Sub Code (Cost Control coding)</label>
                  <select value={form.sub_code} onChange={e=>h('sub_code', e.target.value)}
                    className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm font-mono">
                    <option value="">— None —</option>
                    {subCodes.map(s => <option key={s.sub_code} value={s.sub_code}>{s.sub_code} — {s.description}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-[#6B7280] mb-1">Notes</label>
                <input value={form.notes} onChange={e=>h('notes', e.target.value)}
                  className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm"/>
              </div>
            </div>
            <div className="flex gap-3 p-5 border-t border-[#E5E7EB]">
              <button onClick={() => setShowModal(false)} className="flex-1 py-2 border border-[#E5E7EB] rounded-lg text-sm">Cancel</button>
              <button onClick={handleSave} className="flex-1 py-2 bg-[#1B3A6B] text-white rounded-lg text-sm font-medium">
                {editEntry ? 'Save Changes' : 'Add Entry'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
